import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {deleteUrl, editUrl, getInfoByEditKey, UrlInfo} from "./clients/ApiClient";
import {addToastMessage} from "./stores/MessageStore";
import {MessageSeverity} from "./utils/MessageSnackbar";
import {Backdrop, Box, Button, CircularProgress, Grid, Paper, TextField, Typography} from "@mui/material";
import {DeleteForever, Edit} from "@mui/icons-material";
import {DeleteConfirmationDialog} from "./utils/DeleteConfirmationDialog";

export const EditPage = () => {
  const params = useParams()
  const editKey = params.key!
  const navigate = useNavigate()
  const [urlInfo, setUrlInfo] = useState<UrlInfo>()
  const [newUrl, setNewUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getInfoByEditKey(editKey)
      setUrlInfo(response)
    } catch (err) {
      addToastMessage('Failed to get info', MessageSeverity.error)
    } finally {
      setLoading(false)
    }
  }, [setLoading, editKey])

  const handleEdit = useCallback(async () => {
    setLoading(true)
    try {
      await editUrl({editKey, newUrl})
      await loadData()
      setNewUrl('')
    } catch (err) {
      addToastMessage('Failed to edit the url', MessageSeverity.error)
    } finally {
      setLoading(false)
    }
  }, [setLoading, editKey, newUrl, loadData])

  const handleDelete = useCallback(async () => {
    try {
      await deleteUrl(editKey)
      addToastMessage('Successfully deleted', MessageSeverity.success)
      navigate('/')
    } finally {
      setDeleteConfirmationOpen(false)
    }
  }, [setDeleteConfirmationOpen])

  useEffect(() => {
    loadData()
  }, [loadData])

  if (loading) {
    return <CircularProgress/>
  }

  return (
    <div className="App" style={{marginBottom: 25, marginTop: 25}}>
      <Backdrop open={loading} style={{zIndex: 999}}><CircularProgress color="inherit"/></Backdrop>
      <Grid container>
        <Grid item sm={1} md={3} lg={4}/>
        <Grid item xs={12} sm={10} md={6} lg={4}>
          <Paper
            elevation={1}
            onClick={() => navigate('/')}
            sx={t => ({
              backgroundColor: t.palette.secondary.main,
              padding: 3,
              maxWidth: 250,
              margin: 'auto',
            })}
          >
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <img src="/android-chrome-512x512.png" alt="logo" style={{maxWidth: 37}}/>
              <h1>&nbsp;urly</h1>
            </div>
            <h4 style={{marginTop: -10}}>SHORTENING URLS</h4>
          </Paper>
          <Paper elevation={3} style={{marginTop: 25, marginLeft: 5, marginRight: 5, padding: 25}}>
            <Grid container>
              <Grid item sm={1}/>
              <Grid item xs={12} sm={10}>
                <Typography variant="h3" fontWeight="bold">Current link</Typography>
                <Typography
                  variant="h5"
                  mt={3}
                  sx={{cursor: 'pointer', wordBreak: 'break-all'}}
                  onClick={() => {
                    const url = urlInfo?.url?.startsWith('http') ? urlInfo?.url : `http://${urlInfo?.url}`
                    window.open(url, '_blank')
                  }}>
                  {urlInfo?.url}
                </Typography>
                <TextField
                  defaultValue={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  inputMode="url"
                  label="New Url to shorten"
                  fullWidth
                  sx={{mt: 3}}
                />
              </Grid>
            </Grid>
            <Box sx={{marginTop: 3.5}}>
              <Button
                startIcon={<DeleteForever/>}
                variant="contained"
                onClick={() => setDeleteConfirmationOpen(true)}
                color="error"
              >
                Delete
              </Button>
              <Button
                startIcon={<Edit/>}
                variant="contained"
                onClick={handleEdit}
                color="primary"
                sx={{ml: 2}}
              >
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <DeleteConfirmationDialog
        open={deleteConfirmationOpen}
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirmationOpen(false)}/>
    </div>
  )
}
